import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import AppSelectWrapper from '../../../components/designe/app-select-wrapper/AppSelectWrapper';
import Search from '../../../components/reusable/search/Search';
import { useAccess } from '../../../hooks/useAccess';
import { useDebounce } from '../../../hooks/use-debounce';
import { useLazyLoad } from '../../../hooks/use-lazy-load';
import {
  useLazyGetArchivedSupportRequests,
  useLazyGetSupportRequests,
  supportAdminApi,
  useUpdateSupportRequestStatus,
} from '../../../services/redux/api/supportAdminApi';
import { useDispatch } from 'react-redux';
import {
  REDUX_CACHE,
  SUPPORT_REQUESTS,
} from '../../../services/redux/constants';
import theme from '../../../styles/theme';
import {
  ISupportRequest,
  SupportRequestStatus,
} from '../../../types/interfaces/support-request';
import { SupportRequestDetailsModal } from './SupportRequestDetailsModal';
import {
  getSupportActiveStatusFilterOptions,
  getSupportRequestStatusOptions,
  SUPPORT_ACTIVE_STATUS_ALL,
} from './supportStatus';
import { useStyles } from './SupportRequestsList.styles';

type Props = {
  archived: boolean;
};

function formatDate(dateIso: string) {
  if (!dateIso) {
    return '—';
  }

  const date = new Date(dateIso);
  if (Number.isNaN(date.valueOf())) {
    return dateIso;
  }

  return date.toLocaleString();
}

export function SupportRequestsList(props: Props) {
  const { archived } = props;
  const { t } = useTranslation();
  const { classes } = useStyles(theme);
  const dispatch = useDispatch();
  const { access } = useAccess();

  const canUpdateStatus =
    access?.supportRequests?.canUpdateStatus ??
    access?.supportRequests?.canEdit ??
    access?.supportRequests?.canView ??
    true;

  const [search, setSearch] = useState('');
  const [activeStatusFilter, setActiveStatusFilter] = useState<
    SupportRequestStatus | typeof SUPPORT_ACTIVE_STATUS_ALL
  >(SUPPORT_ACTIVE_STATUS_ALL);
  const [selectedRequest, setSelectedRequest] =
    useState<ISupportRequest | null>(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [updateStatus, { isLoading: isSavingStatus }] =
    useUpdateSupportRequestStatus();

  const debouncedSearch = useDebounce(search);
  const normalizedSearch = debouncedSearch.trim();

  const queryParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      archived,
      limit: 20,
      pageSize: 20,
    };

    if (normalizedSearch) {
      params.search = normalizedSearch;
    }

    if (!archived && activeStatusFilter !== SUPPORT_ACTIVE_STATUS_ALL) {
      params.status = activeStatusFilter;
    }
    params.refreshVersion = refreshVersion;

    return params;
  }, [activeStatusFilter, archived, normalizedSearch, refreshVersion]);

  const { items, footerRef, resetAll, isFetching, error, retryLastRequest } =
    useLazyLoad(
      archived ? useLazyGetArchivedSupportRequests : useLazyGetSupportRequests,
      queryParams,
    );

  const requests = (items as ISupportRequest[] | null) ?? [];
  const queryKey = useMemo(() => JSON.stringify(queryParams), [queryParams]);
  const previousQueryKeyRef = useRef(queryKey);

  useEffect(() => {
    if (queryKey !== previousQueryKeyRef.current) {
      previousQueryKeyRef.current = queryKey;
      resetAll(true);
    }
  }, [queryKey, resetAll]);

  const activeStatusFilterOptions = useMemo(
    () => getSupportActiveStatusFilterOptions(t),
    [t],
  );
  const statusOptions = useMemo(() => getSupportRequestStatusOptions(t), [t]);

  async function onSaveStatus(status: SupportRequestStatus) {
    if (!selectedRequest) {
      return;
    }

    await updateStatus({
      requestId: selectedRequest.id,
      body: { status },
    }).unwrap();

    dispatch(
      supportAdminApi.util.invalidateTags([
        { type: SUPPORT_REQUESTS.SUPPORT_REQUEST, id: selectedRequest.id },
        {
          type: SUPPORT_REQUESTS.ACTIVE_SUPPORT_REQUESTS,
          id: REDUX_CACHE.LIST,
        },
        {
          type: SUPPORT_REQUESTS.ARCHIVED_SUPPORT_REQUESTS,
          id: REDUX_CACHE.LIST,
        },
      ]),
    );

    setSelectedRequest(null);
    setRefreshVersion((prev) => prev + 1);
  }

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.filtersRow}>
        <Search
          fullWidth
          value={search}
          onChange={setSearch}
          className={classes.search}
        />
        {!archived && (
          <AppSelectWrapper
            className={classes.statusFilter}
            label={t('moderatorDashboard.status')}
            options={activeStatusFilterOptions}
            defaultValue={
              activeStatusFilterOptions.find(
                (option) => option.id === activeStatusFilter,
              ) ?? null
            }
            onChange={(event) =>
              setActiveStatusFilter(
                event?.target?.value?.id ?? SUPPORT_ACTIVE_STATUS_ALL,
              )
            }
            disableClear
            disableSorting
          />
        )}
      </Box>

      <Box className={classes.list}>
        {requests.map((request) => (
          <Box
            key={request.id}
            className={classes.listItem}
            onClick={() => setSelectedRequest(request)}>
            <Box className={classes.listItemTop}>
              <Typography variant="text-md-sb">{request.title}</Typography>
              <Typography variant="text-sm" className={classes.listItemMeta}>
                {formatDate(request.createdAt)}
              </Typography>
            </Box>
            <Box className={classes.statusRow}>
              <Typography variant="text-sm" className={classes.listItemMeta}>
                {t('moderatorDashboard.status')}
              </Typography>
              <Typography variant="text-sm-sb" className={classes.statusBadge}>
                {statusOptions.find((option) => option.id === request.status)
                  ?.name ?? request.status}
              </Typography>
            </Box>
            <Typography variant="text-sm">
              {request.name} ({request.email})
            </Typography>
          </Box>
        ))}
      </Box>

      {!isFetching && !requests.length && (
        <Typography className={classes.emptyState}>
          {t('adminDashboard.support.empty')}
        </Typography>
      )}

      {!!error && (
        <Typography className={classes.emptyState}>
          {t('adminDashboard.support.loadError')}{' '}
          <Typography
            component="span"
            variant="text-sm-sb"
            sx={{ cursor: 'pointer' }}
            onClick={() => void retryLastRequest()}>
            {t('adminDashboard.support.retry')}
          </Typography>
        </Typography>
      )}

      {isFetching && (
        <Typography className={classes.loadingState}>
          {t('adminDashboard.support.loading')}
        </Typography>
      )}

      <Box ref={footerRef} className={classes.footer} />

      <SupportRequestDetailsModal
        open={!!selectedRequest}
        request={selectedRequest}
        canUpdateStatus={canUpdateStatus}
        isSaving={isSavingStatus}
        classes={classes}
        onClose={() => setSelectedRequest(null)}
        onSaveStatus={onSaveStatus}
      />
    </Box>
  );
}
